/**
 * ImportantDatesSection — person Overview widget showing birth/death/custom dates
 * with reminder status and quick-add actions.
 *
 * Placed on person Overview tab. Subtle, premium, not crowded.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Bell, BellOff, Plus, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { AddReminderDialog } from './AddReminderDialog';
import { DigitalCardPreview } from './DigitalCardPreview';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  remindersApi, REMINDER_TYPE_LABELS, formatReminderDate, daysUntilCopy, daysUntilColor,
} from '../../utils/reminders';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { formatDisplayDate } from '../../utils/dateDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {{
 *   person: object,
 *   treeId?: string,
 *   canEdit?: boolean,
 * }} props
 */
export function ImportantDatesSection({ person, treeId, canEdit = false }) {
  const [reminders, setReminders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addPrefill, setAddPrefill] = useState({});
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [cardReminder, setCardReminder] = useState(null);
  const [enablingId, setEnablingId] = useState(null);

  const personName = [person?.given_names, person?.surname].filter(Boolean).join(' ') || 'This person';
  const imageUrl = person?.profile_image_url
    ? resolveAssetUrl(BACKEND_URL, person.profile_image_url)
    : null;

  const load = useCallback(async () => {
    if (!person?.id) return;
    setLoading(true);
    try {
      const [rem, sug] = await Promise.all([
        remindersApi.list().then((d) => d.filter((r) => r.person_id === person.id)),
        remindersApi.suggest(person.id),
      ]);
      setReminders(rem);
      setSuggestions(sug.suggestions || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [person?.id]);

  useEffect(() => { load(); }, [load]);

  const hasBirthdate = Boolean(person?.birth_date);
  const hasDeathdate = Boolean(person?.death_date);
  const hasReminders = reminders.length > 0;
  const hasSuggestions = suggestions.length > 0;

  // Quick-enable a suggested reminder with one click
  const handleEnableSuggestion = async (suggestion) => {
    setEnablingId(suggestion.reminder_type);
    try {
      const result = await remindersApi.create({
        ...suggestion,
        tree_id: treeId || null,
        person_id: person.id,
        person_name: personName,
        person_image_url: person?.profile_image_url || null,
        enabled: true,
      });
      setReminders((rs) => [...rs, result]);
      setSuggestions((ss) => ss.filter((s) => s.reminder_type !== suggestion.reminder_type));
      toast.success(`${REMINDER_TYPE_LABELS[suggestion.reminder_type] || 'Reminder'} enabled.`);
    } catch (err) {
      toast.error(err?.message || 'Could not enable reminder.');
    } finally {
      setEnablingId(null);
    }
  };

  const handleToggle = async (reminder) => {
    try {
      const updated = await remindersApi.update(reminder.id, { enabled: !reminder.enabled });
      setReminders((rs) => rs.map((r) => r.id === updated.id ? updated : r));
    } catch { toast.error('Could not update reminder.'); }
  };

  const handleOpenAddCustom = () => {
    setAddPrefill({ person_id: person.id, person_name: personName, person_image_url: person?.profile_image_url, tree_id: treeId });
    setAddDialogOpen(true);
  };

  const handleOpenCard = (reminder) => {
    setCardReminder(reminder);
    setCardDialogOpen(true);
  };

  if (!hasBirthdate && !hasDeathdate && !hasReminders && !hasSuggestions && !loading) {
    if (!canEdit) return null;
    return (
      <section className="rounded-xl border border-dashed border-[hsl(38_14%_86%)] px-4 py-3" data-testid="important-dates-section">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground/60">Add birthdays, remembrance days, or family milestones when you&apos;re ready.</p>
          {canEdit && (
            <Button type="button" variant="ghost" size="sm" className="gap-1.5 rounded-full text-xs" onClick={handleOpenAddCustom}>
              <Plus className="h-3.5 w-3.5" /> Add date
            </Button>
          )}
        </div>
        <AddReminderDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} prefill={addPrefill} onSaved={load} />
      </section>
    );
  }

  return (
    <section className="space-y-3" data-testid="important-dates-section">
      {/* Section heading */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground/60" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
            Important dates
          </span>
        </div>
        {canEdit && (
          <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 rounded-full px-2 text-[11px] text-muted-foreground hover:text-foreground" onClick={handleOpenAddCustom}>
            <Plus className="h-3 w-3" /> Add
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground/60">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      )}

      {/* Known dates from profile */}
      {!loading && (hasBirthdate || hasDeathdate) && (
        <div className="space-y-1.5">
          {hasBirthdate && (
            <DateRow
              icon="🎂"
              label="Birthday"
              rawDate={person.birth_date}
              reminderExists={reminders.some((r) => r.reminder_type === 'birthday')}
              onEnableSuggestion={() => {
                const sug = suggestions.find((s) => s.reminder_type === 'birthday');
                if (sug) handleEnableSuggestion(sug);
                else handleOpenAddCustom();
              }}
              loading={enablingId === 'birthday'}
            />
          )}
          {hasDeathdate && (
            <DateRow
              icon="🕯️"
              label="Remembrance day"
              rawDate={person.death_date}
              reminderExists={reminders.some((r) => ['death_anniversary', 'remembrance_day'].includes(r.reminder_type))}
              onEnableSuggestion={() => {
                const sug = suggestions.find((s) => s.reminder_type === 'death_anniversary');
                if (sug) handleEnableSuggestion(sug);
                else handleOpenAddCustom();
              }}
              loading={enablingId === 'death_anniversary'}
            />
          )}
        </div>
      )}

      {/* Active reminders */}
      {!loading && reminders.length > 0 && (
        <div className="space-y-1.5">
          {reminders.map((r) => (
            <ReminderRow
              key={r.id}
              reminder={r}
              onToggle={() => handleToggle(r)}
              onCard={() => handleOpenCard(r)}
            />
          ))}
        </div>
      )}

      {/* Suggested reminders not yet enabled */}
      {!loading && suggestions.length > 0 && (
        <div className="space-y-1.5">
          {suggestions.map((s) => (
            <div
              key={s.reminder_type}
              className="flex items-center justify-between rounded-lg border border-dashed border-[hsl(38_14%_86%)] bg-muted/20 px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm">{s.reminder_type === 'birthday' ? '🎂' : '🕯️'}</span>
                <div>
                  <p className="text-xs font-medium text-foreground/80">{s.title}</p>
                  <p className="text-[11px] text-muted-foreground/60">
                    {REMINDER_TYPE_LABELS[s.reminder_type]} · {formatReminderDate(s.date_month, s.date_day)}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 rounded-full px-3 text-xs gap-1.5 border border-border/60"
                onClick={() => handleEnableSuggestion(s)}
                disabled={enablingId === s.reminder_type}
              >
                {enablingId === s.reminder_type
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Bell className="h-3 w-3" />}
                Enable
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add reminder dialog */}
      <AddReminderDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        prefill={addPrefill}
        onSaved={load}
      />

      {/* Digital card preview dialog */}
      <Dialog open={cardDialogOpen} onOpenChange={setCardDialogOpen}>
        <DialogContent className="sm:max-w-sm border-[#e8e2d8] bg-[#fffcf7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-normal">Tribute card</DialogTitle>
          </DialogHeader>
          {cardReminder && (
            <DigitalCardPreview
              reminder={{ ...cardReminder, person_image_url: person?.profile_image_url }}
              onClose={() => setCardDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}


function DateRow({ icon, label, rawDate, reminderExists, onEnableSuggestion, loading }) {
  const display = rawDate ? formatDisplayDate(rawDate.split('T')[0]) : '—';

  return (
    <div className="flex items-center justify-between rounded-lg border border-[hsl(38_14%_88%)] bg-muted/10 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-sm leading-none">{icon}</span>
        <div>
          <p className="text-xs font-medium text-foreground/80">{label}</p>
          <p className="text-[11px] text-muted-foreground/60">{display}</p>
        </div>
      </div>
      {reminderExists ? (
        <span className="flex items-center gap-1 text-[11px] text-primary/60">
          <Bell className="h-3 w-3" /> Reminder on
        </span>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 rounded-full px-3 text-xs gap-1 border border-border/60"
          onClick={onEnableSuggestion}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bell className="h-3 w-3" />}
          {label === 'Birthday' ? 'Birthday reminder' : 'Remembrance reminder'}
        </Button>
      )}
    </div>
  );
}


function ReminderRow({ reminder, onToggle, onCard }) {
  const icon = reminder.reminder_type === 'birthday' ? '🎂'
    : ['death_anniversary', 'remembrance_day'].includes(reminder.reminder_type) ? '🕯️'
    : '📅';
  const daysCopy = daysUntilCopy(reminder.days_until);
  const daysColor = daysUntilColor(reminder.days_until);

  return (
    <div className="flex items-center justify-between rounded-lg border border-[hsl(38_14%_88%)] px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="shrink-0 text-sm leading-none">{icon}</span>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-foreground/80">{reminder.title}</p>
          <p className={`text-[11px] ${daysColor}`}>
            {formatReminderDate(reminder.date_month, reminder.date_day, reminder.date_year)}
            {daysCopy && ` · ${daysCopy}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 rounded-full px-2 text-[11px] text-muted-foreground gap-1"
          onClick={onCard}
          title="Create tribute card"
        >
          Card
        </Button>
        <button
          type="button"
          onClick={onToggle}
          className={`h-6 w-6 rounded-full transition-colors ${reminder.enabled ? 'text-primary/70 hover:text-red-500' : 'text-muted-foreground/40 hover:text-primary'}`}
          title={reminder.enabled ? 'Disable reminder' : 'Enable reminder'}
        >
          {reminder.enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default ImportantDatesSection;
